// Characteristics of Sound Waves — rich slide renderer.
//
// Each slide is a self-contained component that reads `fragmentIndex`
// (0-based) and renders content + chart state accordingly. The dispatcher
// at the bottom maps slide.id → component.
//
// All components stay inside the 1920×1080 logical canvas managed by
// LessonStage's ScaledStage; positions use logical pixels.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InlineMath, BlockMath } from "../Math";
import { X } from "lucide-react";
import "katex/dist/katex.min.css";
import WaveChart, { sampleSine } from "./WaveChart";

/* ============ Concept-check / engagement popup ============
   Pushed by the teacher's mobile remote. Renders ONLY when
   `activeQuestion` is non-null in the synced session state — never
   appears automatically. Teacher taps the card on the mobile home to
   show it, taps again to dismiss; that toggle drives `activeQuestion`
   across the WS relay.

   In `/slide-preview` (the mobile WebView preview) we always show the
   embedded preview WITHOUT the popup so the teacher can see slide
   content underneath — the question is shown as a dedicated card on
   the mobile home, not on the embedded preview.
*/
const QuestionPopup = ({ activeQuestion, slideId, fragmentIndex }) => {
    // Suppress when ?embed=remote — the mobile WebView preview never
    // shows the popup; the question is rendered as a card on the
    // RemoteControllerScreen home instead.
    const suppressForRemote = React.useMemo(() => {
        try {
            return (
                new URLSearchParams(window.location.search).get("embed") ===
                "remote"
            );
        } catch {
            return false;
        }
    }, []);

    const visible =
        !!activeQuestion?.text &&
        !suppressForRemote &&
        // Match the current fragment so an old activeQuestion for the
        // previous slide/fragment doesn't bleed through.
        (activeQuestion.slideId == null ||
            activeQuestion.slideId === slideId) &&
        (activeQuestion.fragmentIndex == null ||
            activeQuestion.fragmentIndex === fragmentIndex);

    const kind = activeQuestion?.kind || "concept-check";

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="absolute inset-0 z-40 flex items-center justify-center px-24"
                    style={{
                        background: "rgba(8,16,24,0.55)",
                        backdropFilter: "blur(14px) saturate(140%)",
                    }}
                    data-testid={
                        kind === "concept-check"
                            ? "concept-check-popup"
                            : "engagement-popup"
                    }
                >
                    <motion.div
                        initial={{ scale: 0.94, y: 24 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.96, y: 12 }}
                        transition={{
                            duration: 0.5,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        style={{
                            background: "white",
                            borderRadius: 28,
                            maxWidth: 820,
                            width: "100%",
                            padding: "40px 52px",
                            boxShadow:
                                "0 60px 160px rgba(8,16,24,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
                            position: "relative",
                        }}
                    >
                        <div
                            style={{
                                display: "inline-block",
                                background:
                                    kind === "concept-check"
                                        ? "linear-gradient(135deg, #5BC2CC, #2E8E9A)"
                                        : "linear-gradient(135deg, #FFA987, #C75730)",
                                color: "white",
                                padding: "6px 14px",
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                marginBottom: 18,
                            }}
                        >
                            {kind === "concept-check"
                                ? "Concept check"
                                : "Think with me"}
                        </div>
                        <p
                            style={{
                                fontSize: 26,
                                lineHeight: 1.3,
                                color: "#14202a",
                                fontWeight: 600,
                                letterSpacing: "-0.005em",
                            }}
                        >
                            {activeQuestion.text}
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/* ============ shared helpers ============ */
const Reveal = ({ showAt, fragmentIndex, children, className = "", from = "y" }) => {
    const initial =
        from === "x"
            ? { opacity: 0, x: -24 }
            : from === "scale"
              ? { opacity: 0, scale: 0.95 }
              : { opacity: 0, y: 18 };
    const enter =
        from === "x"
            ? { opacity: 1, x: 0 }
            : from === "scale"
              ? { opacity: 1, scale: 1 }
              : { opacity: 1, y: 0 };
    return (
        <AnimatePresence initial={false}>
            {fragmentIndex >= showAt && (
                <motion.div
                    initial={initial}
                    animate={enter}
                    exit={initial}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className={className}
                    data-mascot-avoid="true"
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const Bg = ({ url, overlay = "rgba(255,255,255,0.9)", children }) => (
    <div className="absolute inset-0 overflow-hidden">
        <img
            src={url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
        />
        <div
            className="absolute inset-0"
            style={{ background: overlay }}
        />
        <div className="relative z-10 w-full h-full px-16 py-14 flex flex-col">
            {children}
        </div>
    </div>
);

const Header = ({ kicker, title, color = "#14202a", kickerColor = "#4a5662" }) => (
    <div className="mb-6" data-mascot-avoid="true">
        <p
            className="uppercase tracking-[0.32em] font-semibold mb-3"
            style={{ color: kickerColor, fontSize: 16 }}
        >
            {kicker}
        </p>
        <h2
            className="font-semibold tracking-tight leading-tight"
            style={{ color, fontSize: 56 }}
        >
            {title}
        </h2>
    </div>
);

const Card = ({ children, style = {}, className = "" }) => (
    <div
        className={className}
        data-mascot-avoid="true"
        style={{
            background: "rgba(255,255,255,0.92)",
            borderRadius: 24,
            border: "1px solid rgba(255,255,255,0.7)",
            boxShadow: "0 24px 60px rgba(15,23,42,0.10)",
            backdropFilter: "blur(16px)",
            padding: 28,
            overflow: "hidden",
            ...style,
        }}
    >
        {children}
    </div>
);

/* ============ Slide 1 · Hook (video background) ============ */
const HookSlide = ({ fragmentIndex }) => (
    <div className="absolute inset-0 overflow-hidden bg-black">
        <video
            src="https://asset.sparkl.me/pb/ai_videos/20260216_191924_6744065c0b6c11f1bc1f026eeea65e6b.mp4"
            autoPlay
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
        />
        <div
            className="absolute inset-0"
            style={{
                background:
                    "rgba(10,15,20,0.55)",
                backdropFilter: "blur(2px)",
            }}
        />
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-24">
            <Reveal showAt={0} fragmentIndex={fragmentIndex} from="scale">
                <h1
                    className="font-semibold tracking-tight text-white mb-8"
                    style={{ fontSize: 112, lineHeight: 1.05 }}
                >
                    The Dance of Sound
                </h1>
                <p
                    className="text-white/85 max-w-4xl mx-auto"
                    style={{ fontSize: 30, lineHeight: 1.4 }}
                >
                    How does this chaotic dance become the music you hear?
                </p>
            </Reveal>
        </div>
    </div>
);

/* ============ Slide 2 · Longitudinal wave + crest/trough ============ */
const LongitudinalSlide = ({ fragmentIndex }) => {
    const points = sampleSine((x) => Math.sin(x), { xMin: 0, xMax: 6.5, step: 0.1 });
    // fragment 1 (index 1): graph + labels appear together (per narration JSON)
    const labels =
        fragmentIndex >= 1
            ? [
                  {
                      x: Math.PI / 2,
                      y: 1,
                      text: "Crest (Compression)",
                      color: "#1e3a8a",
                      dy: -16,
                      align: "center",
                      baseline: "bottom",
                  },
                  {
                      x: (3 * Math.PI) / 2,
                      y: -1,
                      text: "Trough (Rarefaction)",
                      color: "#7c1d1d",
                      dy: 24,
                      align: "center",
                      baseline: "top",
                  },
              ]
            : [];
    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/00bb0602-4536-475f-9c4d-f61d9d5a1b70.jpg"
            overlay="rgba(255,255,255,0.9)"
        >
            <Header kicker="Concept · Longitudinal waves" title="Seeing the Invisible" />
            <div className="flex-1 grid grid-cols-12 gap-12 items-center">
                <div className="col-span-6 space-y-6">
                    <Reveal showAt={0} fragmentIndex={fragmentIndex} from="x">
                        <p style={{ fontSize: 26, lineHeight: 1.5, color: "#14202a" }}>
                            Sound travels as a <strong>longitudinal wave</strong>,
                            creating regions of varying density in the medium.
                        </p>
                    </Reveal>
                    <Reveal showAt={0} fragmentIndex={fragmentIndex} from="x">
                        <div className="grid grid-cols-2 gap-4">
                            <Card style={{ padding: 20 }}>
                                <p
                                    className="uppercase tracking-[0.2em] font-semibold mb-2"
                                    style={{ color: "#1e3a8a", fontSize: 14 }}
                                >
                                    Compression
                                </p>
                                <p style={{ fontSize: 18, color: "#14202a", lineHeight: 1.4 }}>
                                    High pressure; particles are bunched together.
                                </p>
                            </Card>
                            <Card style={{ padding: 20 }}>
                                <p
                                    className="uppercase tracking-[0.2em] font-semibold mb-2"
                                    style={{ color: "#7c1d1d", fontSize: 14 }}
                                >
                                    Rarefaction
                                </p>
                                <p style={{ fontSize: 18, color: "#14202a", lineHeight: 1.4 }}>
                                    Low pressure; particles are spread apart.
                                </p>
                            </Card>
                        </div>
                    </Reveal>
                </div>
                <div className="col-span-6">
                    <Reveal showAt={1} fragmentIndex={fragmentIndex} from="scale">
                        <Card style={{ padding: 24 }}>
                            <div style={{ height: 540 }}>
                                <WaveChart
                                    series={[
                                        {
                                            id: "density",
                                            label: "Density",
                                            color: "#3b82f6",
                                            borderWidth: 3,
                                            points,
                                        },
                                    ]}
                                    xMin={0}
                                    xMax={6.5}
                                    yMin={-2}
                                    yMax={2}
                                    xTitle="Position"
                                    yTitle="Density / Pressure"
                                    yTickStep={1}
                                    labels={labels}
                                />
                            </div>
                            <p
                                className="text-center mt-3"
                                style={{ color: "#4a5662", fontSize: 14 }}
                            >
                                Fig 1. Mapping density variations to a sine curve.
                            </p>
                        </Card>
                    </Reveal>
                </div>
            </div>
        </Bg>
    );
};

/* ============ Slide 3 · Wavelength ============ */
const WavelengthSlide = ({ fragmentIndex }) => {
    const points = sampleSine((x) => Math.sin(x), { xMin: 0, xMax: 14, step: 0.1 });
    const TWO_PI = Math.PI * 2;
    const brackets = [];
    if (fragmentIndex >= 1)
        brackets.push({
            x1: Math.PI / 2,
            x2: Math.PI / 2 + TWO_PI,
            y: 1.45,
            label: "λ",
            color: "#FFA907",
            opacity: 1,
        });
    if (fragmentIndex >= 2) {
        // overwrite with trough-to-trough bracket
        brackets.length = 0;
        brackets.push({
            x1: (3 * Math.PI) / 2,
            x2: (3 * Math.PI) / 2 + TWO_PI,
            y: -1.45,
            label: "λ",
            color: "#FFA907",
            opacity: 1,
            labelBelow: true,
        });
    }
    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/4e72f8c3-9954-4338-ba2e-7a523886807e.jpg"
            overlay="linear-gradient(135deg, rgba(8,32,67,0.85), rgba(12,74,110,0.78))"
        >
            <Header
                kicker="Concept · Wavelength"
                title="Measuring the Wave: Wavelength"
                color="#ffffff"
                kickerColor="rgba(255,255,255,0.75)"
            />
            <div className="flex-1 grid grid-cols-12 gap-12 items-center">
                <div className="col-span-6 space-y-6">
                    <Reveal showAt={0} fragmentIndex={fragmentIndex} from="x">
                        <p
                            style={{
                                color: "white",
                                fontSize: 26,
                                lineHeight: 1.5,
                            }}
                        >
                            The distance between two consecutive compressions (C)
                            or two consecutive rarefactions (R) is called the
                            wavelength{" "}
                            <span style={{ color: "#FFC56A" }}>
                                <InlineMath math="\lambda" />
                            </span>
                            .
                        </p>
                    </Reveal>
                    <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                        <Card
                            style={{
                                background: "rgba(255,255,255,0.12)",
                                border: "1px solid rgba(255,255,255,0.18)",
                                padding: 22,
                            }}
                        >
                            <div className="flex items-center gap-4">
                                <span style={{ fontSize: 40, color: "#FFC56A" }}>
                                    <InlineMath math="\lambda" />
                                </span>
                                <p style={{ color: "white", fontSize: 22 }}>
                                    {fragmentIndex >= 2
                                        ? "Or measured from trough to trough."
                                        : "Measured from crest to crest."}
                                </p>
                            </div>
                        </Card>
                    </Reveal>
                </div>
                <div className="col-span-6">
                    <Card style={{ padding: 24 }}>
                        <div style={{ height: 540 }}>
                            <WaveChart
                                series={[
                                    {
                                        id: "wave",
                                        label: "Sound Wave",
                                        color: "#38bdf8",
                                        borderWidth: 3,
                                        points,
                                    },
                                ]}
                                xMin={0}
                                xMax={14}
                                yMin={-2}
                                yMax={2}
                                hideYTicks
                                brackets={brackets}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </Bg>
    );
};

/* ============ Slide 4 · Frequency & Time period ============ */
const FrequencySlide = ({ fragmentIndex }) => {
    const fullWave = sampleSine((t) => Math.sin(2 * Math.PI * 4 * t), {
        xMin: 0,
        xMax: 1,
        step: 0.005,
    });
    const oneCycle = sampleSine((t) => Math.sin(2 * Math.PI * 4 * t), {
        xMin: 0,
        xMax: 0.25,
        step: 0.005,
    });
    const baseColor =
        fragmentIndex >= 2 ? "rgba(37,99,235,0.35)" : "#2563EB";
    const baseWidth = fragmentIndex >= 1 ? 4 : 3;
    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/197694fe-3133-40dd-b3df-e99b264774a7.jpg"
            overlay="rgba(255,255,255,0.92)"
        >
            <Header kicker="Concept · Frequency & Period" title="The Rhythm of Sound: Frequency & Time" />
            <div className="flex-1 grid grid-cols-12 gap-12 items-start">
                <div className="col-span-5 space-y-5">
                    <Reveal showAt={0} fragmentIndex={fragmentIndex} from="x">
                        <Card>
                            <p
                                className="font-semibold mb-2"
                                style={{ color: "#2563EB", fontSize: 22 }}
                            >
                                Frequency (<InlineMath math="\nu" />)
                            </p>
                            <p style={{ color: "#14202a", fontSize: 22, lineHeight: 1.45 }}>
                                The number of complete oscillations per unit time.
                            </p>
                            <p
                                style={{
                                    color: "#4a5662",
                                    fontSize: 18,
                                    marginTop: 10,
                                }}
                            >
                                Example: 4 cycles in 1 second = <strong>4 Hz</strong>.
                            </p>
                        </Card>
                    </Reveal>
                    <Reveal showAt={2} fragmentIndex={fragmentIndex} from="x">
                        <Card>
                            <p
                                className="font-semibold mb-2"
                                style={{ color: "#DC2626", fontSize: 22 }}
                            >
                                Time Period (<InlineMath math="T" />)
                            </p>
                            <p style={{ color: "#14202a", fontSize: 22, lineHeight: 1.45 }}>
                                The time taken for one complete oscillation.
                            </p>
                            <p
                                style={{
                                    color: "#4a5662",
                                    fontSize: 18,
                                    marginTop: 10,
                                }}
                            >
                                Focus on a single cycle duration.
                            </p>
                        </Card>
                    </Reveal>
                    <Reveal showAt={3} fragmentIndex={fragmentIndex} from="scale">
                        <Card
                            style={{
                                background:
                                    "linear-gradient(135deg, #fef3c7, #fde68a)",
                                border: "1px solid #fde68a",
                            }}
                        >
                            <div style={{ fontSize: 36, textAlign: "center" }}>
                                <BlockMath math={String.raw`T = \frac{1}{\nu}`} />
                            </div>
                            <p
                                className="text-center font-semibold"
                                style={{ color: "#92400e", fontSize: 18, marginTop: 4 }}
                            >
                                Inverse Relationship
                            </p>
                        </Card>
                    </Reveal>
                </div>
                <div className="col-span-7">
                    <Card style={{ padding: 22 }}>
                        <div style={{ height: 540, position: "relative" }}>
                            <WaveChart
                                series={[
                                    {
                                        id: "wave",
                                        color: baseColor,
                                        borderWidth: baseWidth,
                                        points: fullWave,
                                    },
                                    {
                                        id: "cycle",
                                        color: "#DC2626",
                                        borderWidth: 6,
                                        points: oneCycle,
                                        visible: fragmentIndex >= 2,
                                    },
                                ]}
                                xMin={0}
                                xMax={1}
                                yMin={-1.5}
                                yMax={1.5}
                                xTitle="Time (s)"
                                yTitle="Amplitude"
                                xTickStep={0.25}
                                hideYTicks
                            />
                            {fragmentIndex === 1 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                    className="absolute top-3 right-3"
                                    style={{
                                        background: "#2563EB",
                                        color: "white",
                                        padding: "8px 14px",
                                        borderRadius: 999,
                                        fontSize: 14,
                                        fontWeight: 600,
                                    }}
                                >
                                    Counting: 4 Full Cycles in 1s
                                </motion.div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </Bg>
    );
};

/* ============ Slide 5 · Pitch (low vs high) ============ */
const PitchSlide = ({ fragmentIndex }) => {
    const lowWave = sampleSine((x) => 1.5 * Math.sin(x) - 1.5, {
        xMin: 0,
        xMax: 10,
        step: 0.05,
    });
    const highWave = sampleSine((x) => 0.8 * Math.sin(3 * x) + 2, {
        xMin: 0,
        xMax: 10,
        step: 0.05,
    });
    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/b0b2abb2-1cda-4bb1-b554-3552113d4f6b.jpg"
            overlay="rgba(15,23,42,0.78)"
        >
            <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                <div
                    className="mx-auto text-center mb-6"
                    data-mascot-avoid="true"
                    style={{
                        background: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.16)",
                        borderRadius: 24,
                        padding: "18px 30px",
                        maxWidth: 1300,
                    }}
                >
                    <p
                        style={{
                            color: "white",
                            fontSize: 30,
                            lineHeight: 1.35,
                        }}
                    >
                        Pitch is how the brain interprets the{" "}
                        <strong style={{ color: "#FFC56A" }}>frequency</strong>{" "}
                        of an emitted sound.
                    </p>
                </div>
            </Reveal>
            <div className="flex-1 grid grid-cols-12 gap-8 items-center">
                <div className="col-span-3">
                    <Reveal showAt={1} fragmentIndex={fragmentIndex} from="x">
                        <Card
                            style={{
                                background: "rgba(96,165,250,0.18)",
                                border: "1px solid rgba(96,165,250,0.4)",
                            }}
                        >
                            <p
                                className="uppercase tracking-[0.2em] font-semibold mb-2"
                                style={{ color: "#60a5fa", fontSize: 14 }}
                            >
                                Low Pitch
                            </p>
                            <p style={{ color: "white", fontSize: 18, lineHeight: 1.45 }}>
                                Long wavelength.
                                <br />
                                Deep sound like a Drum.
                            </p>
                        </Card>
                    </Reveal>
                </div>
                <div className="col-span-6">
                    <Card style={{ padding: 18 }}>
                        <div style={{ height: 460 }}>
                            <WaveChart
                                series={[
                                    {
                                        id: "low",
                                        color: "#60a5fa",
                                        borderWidth: 4,
                                        points: lowWave,
                                    },
                                    {
                                        id: "high",
                                        color: "#f87171",
                                        borderWidth: 4,
                                        points: highWave,
                                        visible: fragmentIndex >= 2,
                                    },
                                ]}
                                xMin={0}
                                xMax={10}
                                yMin={-4}
                                yMax={4}
                                xTickStep={2}
                                yTickStep={2}
                            />
                        </div>
                    </Card>
                </div>
                <div className="col-span-3">
                    <Reveal showAt={2} fragmentIndex={fragmentIndex} from="x">
                        <Card
                            style={{
                                background: "rgba(248,113,113,0.18)",
                                border: "1px solid rgba(248,113,113,0.4)",
                            }}
                        >
                            <p
                                className="uppercase tracking-[0.2em] font-semibold mb-2"
                                style={{ color: "#fca5a5", fontSize: 14 }}
                            >
                                High Pitch
                            </p>
                            <p style={{ color: "white", fontSize: 18, lineHeight: 1.45 }}>
                                Short wavelength.
                                <br />
                                Shrill sound like a Whistle.
                            </p>
                        </Card>
                    </Reveal>
                </div>
            </div>
            <Reveal showAt={3} fragmentIndex={fragmentIndex}>
                <div
                    className="mx-auto mt-6 text-center"
                    data-mascot-avoid="true"
                    style={{
                        background:
                            "linear-gradient(135deg, #f59e0b, #ef4444)",
                        color: "white",
                        padding: "14px 30px",
                        borderRadius: 999,
                        fontSize: 22,
                        fontWeight: 600,
                        maxWidth: 900,
                        boxShadow: "0 20px 50px rgba(239,68,68,0.4)",
                    }}
                >
                    Faster vibration = Higher Frequency = Higher Pitch
                </div>
            </Reveal>
        </Bg>
    );
};

/* ============ Slide 6 · Amplitude & Loudness ============ */
const AmplitudeSlide = ({ fragmentIndex }) => {
    const amplitude = fragmentIndex >= 2 ? 2.5 : 0.5;
    const lineColor =
        fragmentIndex >= 2 ? "#2563EB" : "rgba(148,163,184,0.95)";
    const lineWidth = fragmentIndex >= 2 ? 5 : 3;
    const wave = sampleSine((x) => amplitude * Math.sin(1.5 * x), {
        xMin: 0,
        xMax: 10,
        step: 0.05,
    });
    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/8a6519a6-fe9d-4fb9-8334-e55717c8cb9d.jpg"
            overlay="linear-gradient(135deg, rgba(255,255,255,0.78), rgba(241,245,249,0.95))"
        >
            <Header kicker="Concept · Amplitude" title="Volume Control: Amplitude & Loudness" />
            <div className="flex-1 grid grid-cols-12 gap-12 items-center">
                <div className="col-span-5 space-y-5">
                    <Reveal showAt={0} fragmentIndex={fragmentIndex} from="x">
                        <Card>
                            <p style={{ fontSize: 24, color: "#14202a", lineHeight: 1.45 }}>
                                <strong>Loudness</strong> is determined by the{" "}
                                <strong style={{ color: "#2563EB" }}>amplitude</strong>{" "}
                                of the sound wave.
                            </p>
                        </Card>
                    </Reveal>
                    <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                        <Card style={{ padding: 22 }}>
                            <p
                                className="font-semibold uppercase tracking-[0.18em] mb-2"
                                style={{
                                    color: fragmentIndex >= 2 ? "#94a3b8" : "#475569",
                                    fontSize: 14,
                                }}
                            >
                                Small Amplitude
                            </p>
                            <p
                                style={{
                                    fontSize: 20,
                                    color: fragmentIndex >= 2 ? "#94a3b8" : "#14202a",
                                }}
                            >
                                Results in a soft sound.
                            </p>
                        </Card>
                    </Reveal>
                    <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                        <Card
                            style={{
                                background:
                                    "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                                border: "1px solid #bfdbfe",
                                padding: 22,
                            }}
                        >
                            <p
                                className="font-semibold uppercase tracking-[0.18em] mb-2"
                                style={{ color: "#1d4ed8", fontSize: 14 }}
                            >
                                Large Amplitude
                            </p>
                            <p style={{ fontSize: 20, color: "#1e3a8a" }}>
                                Results in a loud sound.
                            </p>
                        </Card>
                    </Reveal>
                    <Reveal showAt={3} fragmentIndex={fragmentIndex} from="scale">
                        <Card
                            style={{
                                background:
                                    "linear-gradient(135deg, #fde68a, #fcd34d)",
                                border: "1px solid #fcd34d",
                                padding: 22,
                            }}
                        >
                            <p
                                style={{ fontSize: 17, color: "#7c2d12", marginBottom: 6 }}
                            >
                                Loudness is proportional to the square of the amplitude.
                            </p>
                            <div style={{ fontSize: 26 }}>
                                <BlockMath math={String.raw`\text{Loudness} \propto \text{Amplitude}^2`} />
                            </div>
                        </Card>
                    </Reveal>
                </div>
                <div className="col-span-7">
                    <Reveal showAt={1} fragmentIndex={fragmentIndex} from="scale">
                        <Card style={{ padding: 22 }}>
                            <p
                                className="uppercase tracking-[0.18em] font-semibold mb-3"
                                style={{ color: "#4a5662", fontSize: 14 }}
                            >
                                Amplitude vs Time
                            </p>
                            <div style={{ height: 540 }}>
                                <WaveChart
                                    series={[
                                        {
                                            id: "wave",
                                            color: lineColor,
                                            borderWidth: lineWidth,
                                            points: wave,
                                        },
                                    ]}
                                    xMin={0}
                                    xMax={10}
                                    yMin={-4}
                                    yMax={4}
                                    hideXTicks
                                    hideYTicks
                                />
                            </div>
                        </Card>
                    </Reveal>
                </div>
            </div>
        </Bg>
    );
};

/* ============ Slide 7 · Timbre ============ */
const TimbreSlide = ({ fragmentIndex }) => {
    const wave =
        fragmentIndex >= 1
            ? sampleSine(
                  (x) =>
                      Math.sin(2 * x) +
                      0.5 * Math.sin(4 * x) +
                      0.25 * Math.sin(6 * x),
                  { xMin: 0, xMax: 10, step: 0.04 },
              )
            : sampleSine((x) => Math.sin(2 * x), {
                  xMin: 0,
                  xMax: 10,
                  step: 0.04,
              });
    const isComplex = fragmentIndex >= 1;
    return (
        <Bg
            url="https://asset.sparkl.me/pb/ai-images/img/7e0090e1-faae-48e1-8246-84ec6c25507f.jpg"
            overlay="linear-gradient(135deg, rgba(46,16,101,0.92), rgba(124,58,237,0.78))"
        >
            <Header
                kicker="Concept · Timbre"
                title="The Signature of Sound: Timbre"
                color="#ffffff"
                kickerColor="rgba(255,255,255,0.7)"
            />
            <div className="flex-1 grid grid-cols-12 gap-12 items-center">
                <div className="col-span-5 space-y-6">
                    <Reveal showAt={0} fragmentIndex={fragmentIndex} from="x">
                        <p
                            style={{
                                color: "white",
                                fontSize: 28,
                                lineHeight: 1.4,
                                fontWeight: 600,
                            }}
                        >
                            Why does a violin sound different from a flute playing
                            the same note?
                        </p>
                    </Reveal>
                    <Reveal showAt={0} fragmentIndex={fragmentIndex} from="x">
                        <Card
                            style={{
                                background: "rgba(255,255,255,0.1)",
                                border: "1px solid rgba(255,255,255,0.2)",
                            }}
                        >
                            <p style={{ color: "white", fontSize: 22, lineHeight: 1.45 }}>
                                A tuning fork produces a{" "}
                                <strong style={{ color: "#22d3ee" }}>
                                    pure sine wave
                                </strong>{" "}
                                consisting of a single frequency.
                            </p>
                        </Card>
                    </Reveal>
                    <Reveal showAt={1} fragmentIndex={fragmentIndex} from="x">
                        <Card
                            style={{
                                background: "rgba(192,132,252,0.18)",
                                border: "1px solid rgba(192,132,252,0.4)",
                            }}
                        >
                            <p
                                className="uppercase tracking-[0.18em] font-semibold mb-2"
                                style={{ color: "#e9d5ff", fontSize: 14 }}
                            >
                                Complex Tones
                            </p>
                            <p style={{ color: "white", fontSize: 20, lineHeight: 1.4 }}>
                                Musical instruments produce complex waves.{" "}
                                <strong>Timbre (quality)</strong> depends on the
                                waveform shape — a mixture of the fundamental
                                frequency with various harmonics.
                            </p>
                        </Card>
                    </Reveal>
                </div>
                <div className="col-span-7">
                    <Card
                        style={{
                            background: "rgba(255,255,255,0.95)",
                            padding: 22,
                        }}
                    >
                        <div
                            className="flex items-center justify-between mb-3"
                            style={{ minHeight: 36 }}
                        >
                            <span
                                className="uppercase tracking-[0.22em] font-bold"
                                style={{
                                    color: isComplex ? "#7c3aed" : "#0e7490",
                                    fontSize: 14,
                                }}
                            >
                                {isComplex ? "Complex Tone" : "Pure Tone"}
                            </span>
                        </div>
                        <div style={{ height: 500 }}>
                            <WaveChart
                                series={[
                                    {
                                        id: "wave",
                                        color: isComplex ? "#c084fc" : "#22d3ee",
                                        borderWidth: 4,
                                        points: wave,
                                    },
                                ]}
                                xMin={0}
                                xMax={10}
                                yMin={-2.5}
                                yMax={2.5}
                                hideXTicks
                                hideYTicks
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </Bg>
    );
};

/* ============ Slide 8 · Wave equation derivation ============ */
const WaveEquationSlide = ({ fragmentIndex }) => (
    <Bg
        url="https://asset.sparkl.me/pb/ai-images/img/ca1a4f26-cfa0-425a-8d20-b59faad19ca4.jpg"
        overlay="rgba(255,255,255,0.94)"
    >
        <Header kicker="Concept · Wave Equation" title="Connecting the Dots" />
        <div className="flex-1 grid grid-cols-2 gap-6 items-stretch max-w-5xl mx-auto w-full">
            <Reveal showAt={0} fragmentIndex={fragmentIndex}>
                <Card className="h-full">
                    <p
                        className="font-semibold mb-3"
                        style={{ color: "#2563EB", fontSize: 16 }}
                    >
                        1 · Speed is distance per unit time
                    </p>
                    <div style={{ fontSize: 32 }}>
                        <BlockMath math={String.raw`v = \frac{\text{Distance}}{\text{Time}}`} />
                    </div>
                </Card>
            </Reveal>
            <Reveal showAt={1} fragmentIndex={fragmentIndex}>
                <Card className="h-full">
                    <p
                        className="font-semibold mb-3"
                        style={{ color: "#2563EB", fontSize: 16 }}
                    >
                        2 · For a single wave cycle
                    </p>
                    <p style={{ fontSize: 22, color: "#14202a", marginBottom: 4 }}>
                        Distance ={" "}
                        <strong style={{ color: "#7c3aed" }}>
                            <InlineMath math="\lambda" />
                        </strong>
                    </p>
                    <p style={{ fontSize: 22, color: "#14202a" }}>
                        Time ={" "}
                        <strong style={{ color: "#7c3aed" }}>
                            <InlineMath math="T" />
                        </strong>
                    </p>
                </Card>
            </Reveal>
            <Reveal showAt={2} fragmentIndex={fragmentIndex}>
                <Card className="h-full">
                    <p
                        className="font-semibold mb-3"
                        style={{ color: "#2563EB", fontSize: 16 }}
                    >
                        3 · Substitute
                    </p>
                    <div style={{ fontSize: 32 }}>
                        <BlockMath math={String.raw`v = \frac{\lambda}{T}`} />
                    </div>
                </Card>
            </Reveal>
            <Reveal showAt={3} fragmentIndex={fragmentIndex} from="scale">
                <Card
                    className="h-full"
                    style={{
                        background:
                            "linear-gradient(135deg, #d1fae5, #a7f3d0)",
                        border: "1px solid #6ee7b7",
                    }}
                >
                    <p
                        className="font-semibold mb-3"
                        style={{ color: "#065f46", fontSize: 16 }}
                    >
                        4 · Since <InlineMath math="\nu = 1/T" />, we get
                    </p>
                    <div style={{ fontSize: 38 }}>
                        <BlockMath math={String.raw`v = \lambda \times \nu`} />
                    </div>
                </Card>
            </Reveal>
        </div>
    </Bg>
);

/* ============ Slide 9 · Worked example ============ */
const WorkedExampleSlide = ({ fragmentIndex }) => (
    <Bg
        url="https://asset.sparkl.me/pb/ai-images/img/34b65564-c219-416e-8ed0-814365adc4ee.jpg"
        overlay="rgba(255,255,255,0.94)"
    >
        <Header kicker="Practice · Worked example" title="Let's Solve It" />
        <div className="flex-1 grid grid-cols-12 gap-6 items-start">
            <Reveal showAt={0} fragmentIndex={fragmentIndex} className="col-span-12">
                <Card
                    style={{
                        background:
                            "linear-gradient(135deg, #fef3c7, #fde68a)",
                        border: "1px solid #fde68a",
                    }}
                >
                    <p
                        className="uppercase tracking-[0.22em] font-bold mb-2"
                        style={{ color: "#92400e", fontSize: 14 }}
                    >
                        Problem Statement
                    </p>
                    <p style={{ fontSize: 22, color: "#14202a", lineHeight: 1.5 }}>
                        A sound wave has a frequency of{" "}
                        <InlineMath math="2 \text{ kHz}" /> and a wavelength of{" "}
                        <InlineMath math="35 \text{ cm}" />. How long will it take
                        to travel <InlineMath math="1.5 \text{ km}" />?
                    </p>
                </Card>
            </Reveal>
            <Reveal showAt={1} fragmentIndex={fragmentIndex} className="col-span-4">
                <Card className="h-full">
                    <StepHeader n={1} label="Given Data" color="#2563EB" />
                    <Row label="Frequency (ν)" math={`2 \\text{ kHz} = 2000 \\text{ Hz}`} />
                    <Row label="Wavelength (λ)" math={`35 \\text{ cm} = 0.35 \\text{ m}`} />
                    <Row label="Distance (d)" math={`1.5 \\text{ km} = 1500 \\text{ m}`} />
                </Card>
            </Reveal>
            <Reveal showAt={2} fragmentIndex={fragmentIndex} className="col-span-4">
                <Card className="h-full">
                    <StepHeader n={2} label="Find Speed (v)" color="#7c3aed" />
                    <p style={{ fontSize: 13, color: "#4a5662", marginTop: 6 }}>Formula</p>
                    <div style={{ fontSize: 24 }}>
                        <BlockMath math={String.raw`v = \lambda \times \nu`} />
                    </div>
                    <p style={{ fontSize: 13, color: "#4a5662", marginTop: 4 }}>Calculation</p>
                    <div style={{ fontSize: 22 }}>
                        <BlockMath math={String.raw`v = 0.35 \times 2000`} />
                        <BlockMath math={String.raw`v = 700 \text{ m/s}`} />
                    </div>
                </Card>
            </Reveal>
            <Reveal showAt={3} fragmentIndex={fragmentIndex} className="col-span-4">
                <Card
                    className="h-full"
                    style={{
                        background:
                            "linear-gradient(135deg, #d1fae5, #a7f3d0)",
                        border: "1px solid #6ee7b7",
                    }}
                >
                    <StepHeader n={3} label="Find Time (t)" color="#065f46" />
                    <p style={{ fontSize: 13, color: "#065f46", marginTop: 6 }}>Formula</p>
                    <div style={{ fontSize: 24 }}>
                        <BlockMath math={String.raw`t = \frac{d}{v}`} />
                    </div>
                    <p style={{ fontSize: 13, color: "#065f46", marginTop: 4 }}>Calculation</p>
                    <div style={{ fontSize: 22 }}>
                        <BlockMath math={String.raw`t = \frac{1500}{700}`} />
                        <BlockMath math={String.raw`t \approx 2.14 \text{ s}`} />
                    </div>
                </Card>
            </Reveal>
        </div>
    </Bg>
);

const StepHeader = ({ n, label, color }) => (
    <div className="flex items-center gap-3 mb-2">
        <div
            style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                background: color,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 16,
            }}
        >
            {n}
        </div>
        <p
            className="font-semibold"
            style={{ color, fontSize: 18 }}
        >
            {label}
        </p>
    </div>
);

const Row = ({ label, math }) => (
    <div style={{ marginTop: 8 }}>
        <p style={{ fontSize: 13, color: "#4a5662" }}>{label}</p>
        <div style={{ fontSize: 19 }}>
            <BlockMath math={math} />
        </div>
    </div>
);

/* ============ Slide 10 · Native interactive simulator ============ */
const SimulatorSlide = () => {
    const [freq, setFreq] = React.useState(1.5);
    const [amp, setAmp] = React.useState(1);
    // Target frequencies / amplitudes the learner must match
    const targetFreq = 3;
    const targetAmp = 2;
    const userWave = sampleSine((x) => amp * Math.sin(freq * x), {
        xMin: 0,
        xMax: 10,
        step: 0.04,
    });
    const targetWave = sampleSine(
        (x) => targetAmp * Math.sin(targetFreq * x),
        { xMin: 0, xMax: 10, step: 0.04 },
    );
    const matched =
        Math.abs(freq - targetFreq) < 0.15 && Math.abs(amp - targetAmp) < 0.15;
    return (
        <div className="absolute inset-0 overflow-hidden ambient-bg">
            <div className="relative z-10 w-full h-full px-16 py-12 flex flex-col">
                <Header kicker="Practice · Interactive simulator" title="Match the Wave" />
                <div className="flex-1 grid grid-cols-12 gap-10 items-stretch">
                    <div className="col-span-8">
                        <Card style={{ padding: 22, height: "100%" }}>
                            <div className="flex items-center justify-between mb-2">
                                <p
                                    className="uppercase tracking-[0.2em] font-semibold"
                                    style={{ color: "#4a5662", fontSize: 14 }}
                                >
                                    Target (dashed) vs Your wave
                                </p>
                                {matched && (
                                    <span
                                        style={{
                                            background: "#dcfce7",
                                            color: "#15803d",
                                            padding: "6px 14px",
                                            borderRadius: 999,
                                            fontWeight: 700,
                                            fontSize: 14,
                                        }}
                                    >
                                        Matched ✓
                                    </span>
                                )}
                            </div>
                            <div style={{ height: 620 }}>
                                <WaveChart
                                    series={[
                                        {
                                            id: "target",
                                            color: "rgba(148,163,184,0.7)",
                                            borderWidth: 3,
                                            points: targetWave,
                                        },
                                        {
                                            id: "user",
                                            color: matched ? "#15803d" : "#2563EB",
                                            borderWidth: 5,
                                            points: userWave,
                                        },
                                    ]}
                                    xMin={0}
                                    xMax={10}
                                    yMin={-3}
                                    yMax={3}
                                    hideXTicks
                                    hideYTicks
                                />
                            </div>
                        </Card>
                    </div>
                    <div className="col-span-4 space-y-5">
                        <Card>
                            <p
                                className="uppercase tracking-[0.2em] font-semibold mb-3"
                                style={{ color: "#2563EB", fontSize: 14 }}
                            >
                                Frequency
                            </p>
                            <input
                                type="range"
                                min={0.5}
                                max={5}
                                step={0.1}
                                value={freq}
                                onChange={(e) => setFreq(parseFloat(e.target.value))}
                                className="w-full"
                                data-testid="sim-frequency-slider"
                            />
                            <p
                                className="text-right"
                                style={{ color: "#14202a", fontSize: 22, fontWeight: 700 }}
                            >
                                {freq.toFixed(1)}
                            </p>
                        </Card>
                        <Card>
                            <p
                                className="uppercase tracking-[0.2em] font-semibold mb-3"
                                style={{ color: "#7c3aed", fontSize: 14 }}
                            >
                                Amplitude
                            </p>
                            <input
                                type="range"
                                min={0.2}
                                max={3}
                                step={0.1}
                                value={amp}
                                onChange={(e) => setAmp(parseFloat(e.target.value))}
                                className="w-full"
                                data-testid="sim-amplitude-slider"
                            />
                            <p
                                className="text-right"
                                style={{ color: "#14202a", fontSize: 22, fontWeight: 700 }}
                            >
                                {amp.toFixed(1)}
                            </p>
                        </Card>
                        <Card
                            style={{
                                background: matched ? "#dcfce7" : "#fef3c7",
                                border: `1px solid ${matched ? "#86efac" : "#fde68a"}`,
                            }}
                        >
                            <p
                                style={{
                                    color: matched ? "#15803d" : "#92400e",
                                    fontSize: 16,
                                    lineHeight: 1.4,
                                }}
                            >
                                {matched
                                    ? "You matched the target wave! Notice how both frequency and amplitude had to line up."
                                    : "Slide both controls until your blue wave hugs the grey target."}
                            </p>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ============ Slide 11 · Recap ============ */
const RecapSlide = ({ fragmentIndex }) => (
    <Bg
        url="https://asset.sparkl.me/pb/ai-images/img/d20770d5-e97a-4f10-995e-5f4cdc24130b.jpg"
        overlay="linear-gradient(135deg, rgba(10,15,20,0.75), rgba(30,41,59,0.7))"
    >
        <Header
            kicker="Recap"
            title="The Music of Physics"
            color="#ffffff"
            kickerColor="rgba(255,255,255,0.7)"
        />
        <div className="flex-1 flex flex-col items-center justify-center max-w-6xl mx-auto w-full space-y-8 text-center">
            <Reveal showAt={0} fragmentIndex={fragmentIndex} from="scale">
                <p
                    className="italic"
                    style={{
                        color: "rgba(255,255,255,0.85)",
                        fontSize: 36,
                    }}
                >
                    "Remember that dancing paint?"
                </p>
            </Reveal>
            <div className="grid grid-cols-2 gap-8 w-full">
                <Reveal showAt={1} fragmentIndex={fragmentIndex} from="x">
                    <Card
                        style={{
                            background: "rgba(96,165,250,0.18)",
                            border: "1px solid rgba(96,165,250,0.4)",
                            padding: 30,
                        }}
                    >
                        <p
                            className="uppercase tracking-[0.22em] font-semibold mb-3"
                            style={{ color: "#bfdbfe", fontSize: 14 }}
                        >
                            The height of the jump
                        </p>
                        <p
                            style={{ color: "white", fontSize: 30, fontWeight: 700 }}
                        >
                            Amplitude (Loudness)
                        </p>
                    </Card>
                </Reveal>
                <Reveal showAt={2} fragmentIndex={fragmentIndex} from="x">
                    <Card
                        style={{
                            background: "rgba(248,113,113,0.18)",
                            border: "1px solid rgba(248,113,113,0.4)",
                            padding: 30,
                        }}
                    >
                        <p
                            className="uppercase tracking-[0.22em] font-semibold mb-3"
                            style={{ color: "#fecaca", fontSize: 14 }}
                        >
                            The speed of the dance
                        </p>
                        <p
                            style={{ color: "white", fontSize: 30, fontWeight: 700 }}
                        >
                            Frequency (Pitch)
                        </p>
                    </Card>
                </Reveal>
            </div>
            <Reveal showAt={3} fragmentIndex={fragmentIndex}>
                <div
                    data-mascot-avoid="true"
                    style={{
                        background:
                            "linear-gradient(135deg, #f59e0b, #ef4444)",
                        color: "white",
                        padding: "18px 36px",
                        borderRadius: 999,
                        fontSize: 24,
                        fontWeight: 600,
                        boxShadow: "0 28px 60px rgba(239,68,68,0.45)",
                    }}
                >
                    Together, they create the unique character of every sound we hear.
                </div>
            </Reveal>
        </div>
    </Bg>
);

/* ============ dispatcher ============ */
export const isSoundWaveRichSlide = (slideId) =>
    [
        "sw-hook",
        "sw-longitudinal",
        "sw-wavelength",
        "sw-frequency",
        "sw-pitch",
        "sw-amplitude",
        "sw-timbre",
        "sw-wave-equation",
        "sw-worked-example",
        "sw-simulator",
        "sw-recap",
    ].includes(slideId);

const SoundWaveSlide = ({ slide, fragmentIndex, activeQuestion }) => {
    const inner = (() => {
        switch (slide?.id) {
            case "sw-hook":
                return <HookSlide fragmentIndex={fragmentIndex} />;
            case "sw-longitudinal":
                return <LongitudinalSlide fragmentIndex={fragmentIndex} />;
            case "sw-wavelength":
                return <WavelengthSlide fragmentIndex={fragmentIndex} />;
            case "sw-frequency":
                return <FrequencySlide fragmentIndex={fragmentIndex} />;
            case "sw-pitch":
                return <PitchSlide fragmentIndex={fragmentIndex} />;
            case "sw-amplitude":
                return <AmplitudeSlide fragmentIndex={fragmentIndex} />;
            case "sw-timbre":
                return <TimbreSlide fragmentIndex={fragmentIndex} />;
            case "sw-wave-equation":
                return <WaveEquationSlide fragmentIndex={fragmentIndex} />;
            case "sw-worked-example":
                return <WorkedExampleSlide fragmentIndex={fragmentIndex} />;
            case "sw-simulator":
                return <SimulatorSlide />;
            case "sw-recap":
                return <RecapSlide fragmentIndex={fragmentIndex} />;
            default:
                return null;
        }
    })();
    return (
        <>
            {inner}
            <QuestionPopup
                activeQuestion={activeQuestion}
                slideId={slide?.id}
                fragmentIndex={fragmentIndex}
            />
        </>
    );
};

export default SoundWaveSlide;
